import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import loadable from '@loadable/component';

import { Lang } from '@common/typescript/objects/Lang';
import { parseQuery } from '@common/typescript/utils/url';
import {
	defaultTransformFiltersBeforeHandleUrl,
	ItemsProvider,
	useItemsProviderContext,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { loadableDelay } from '@common/react/loadable/loadableSettings';
import Button from '@common/react/components/Forms/Button';

import { capitalizeFirstLetter } from '@app/components/Utils';
import { getPageInfo } from '@app/objects/Page';
import LinkWithLang from '@app/components/UI/LinkWithLang/LinkWithLang';
import { Blog as BlogInterface } from '@app/objects/Pages/BlogPage';
import { SimpleBreadCrumbs } from '@app/components/UI/SimpleBreadcrumbs/SimpleBreadcrumbs';
import ServerPageProvider from '@app/components/UI/SeverPageProviderWithLang/ServerpageProviderWithLang';
import '@app/scss/pages/blog.scss';

const Pagination = loadable(
	() =>
		loadableDelay(import(/* webpackChunkName: "Pagination" */ '@app/components/UI/CustomPagination')),
	{
		fallback: (
			<div className="text-center">
				<Button className="btn-sm btn btn-primary" isLoading>
					Loading
				</Button>
			</div>),
	},
);

interface BlogInnerProps {
	lang: Lang;
}

const BlogInner: React.FC<BlogInnerProps> = ({
	lang,
}) => {
	const context = useItemsProviderContext<BlogInterface>();

	if (!context.state) throw 'Need ItemsProvider context!';

	const { state: { items, pagination }, actions: { handleChange } } = context;

	const { t, i18n } = useTranslation();
	const langString = capitalizeFirstLetter(i18n.language);

	return <>
		{items && <>
			{items.map((item: BlogInterface, index: number) => {
				const pageInfo = getPageInfo(item, lang);
				return (
					<div className="post" key={index}>
						<LinkWithLang to={`/${item[langString === 'En' ? 'fullPath' : 'fullPathEs']}`} className="post">
							<h3 className="post__title">{item[`shortName${langString}`]}</h3>
						</LinkWithLang>
						<div className="external-html" dangerouslySetInnerHTML={{ __html: pageInfo ? pageInfo.shortBody : '' }} />
						<LinkWithLang
							to={`/${item[langString === 'En' ? 'fullPath' : 'fullPathEs']}`}
							className="general-form-button general-form-button_sm"
						>
							{t('site.Read More')}
						</LinkWithLang>
					</div>
				);
			})}
			{pagination.total > 10
				&& <Pagination
					className="dark-pagination"
					defaultCurrent={pagination.current}
					total={pagination.total ? pagination.total : 0}
					onChange={(page) => handleChange({ page })}
				/>
			}
		</>}
	</>;
};

const Blog: React.FC = () => {
	const { t, i18n } = useTranslation();
	const location = useLocation();
	const lang = i18n.language === 'es' ? Lang.Es : Lang.En;

	const search = parseQuery(location.search);
	const defCurrent = +search.page || 1;

	return (
		<div className="blog-page inner-page container">
			<SimpleBreadCrumbs title={t('seo.blogPage.title')} />
			<ServerPageProvider
				path="blog"
				inner={(serverPage) =>
					<ItemsProvider
						items={serverPage?.items?.list}
						pagination={{ current: defCurrent, pageSize: 10, total: serverPage?.items?.count }}
						type="blog"
						loadRequest="blogListRemote"
						filters={{ page: defCurrent, withRows: true }}
						withHandleUrl
						pageInSearch
						transformFiltersBeforeHandleUrl={(filters) => ({
							...defaultTransformFiltersBeforeHandleUrl(filters),
							page: filters.page || 1,
						})}
					>
						<BlogInner
							lang={lang}
						/>
					</ItemsProvider>
				}
			/>
		</div>
	);
};

export default Blog;
